import type React from 'react';
import type { BreadcrumbItem } from '../ui/Breadcrumbs.js';
import { PageHeader, type PageHelpContent } from '../ui/PageHeader.js';
import { StatStrip, type StatStripCell } from '../ui/StatStrip.js';
import { cn } from '../../utils/cn.js';

interface DashboardPageProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  helpContent?: PageHelpContent;
  /** Period select, refresh, "Add widget". */
  actions?: React.ReactNode;
  /** The headline numbers (NextAdmin overview stats). */
  stats?: StatStripCell[];
  alert?: React.ReactNode;
  /** `ChartCard`s and other panels; lay them out with `DashboardGrid`. */
  children: React.ReactNode;
}

/** The dashboard template (NextAdmin home): header, headline numbers, then a grid of chart and table cards. */
export function DashboardPage({
  title,
  subtitle,
  breadcrumbs,
  helpContent,
  actions,
  stats,
  alert,
  children,
}: DashboardPageProps) {
  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        helpContent={helpContent}
        actions={actions}
      />
      {stats && stats.length > 0 && <StatStrip cells={stats} className="mb-5" />}
      {alert}
      {children}
    </div>
  );
}

/** Dashboard panels: one column on phones, 12 columns from `xl`; size each child with `xl:col-span-*`. */
export function DashboardGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-1 gap-5 xl:grid-cols-12', className)}>{children}</div>;
}
